import { ChangeEvent, forwardRef, InputHTMLAttributes, ReactNode, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FieldPath, UseFormRegister, UseFormRegisterReturn, UseFormTrigger } from 'react-hook-form';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { RegisterOptions } from 'react-hook-form/dist/types/validator';
import Minus from '~/icons/minus.svg';
import Selected from '~/icons/selected.svg';
import { RawHtml } from '~/shared/components/RawHtml/RawHtml';
import { useInputField } from '../../hooks/useInputField';
import { HelpText } from '../HelpText/HelpText';
import { InvalidMessage } from '../InvalidMessage/InvalidMessage';
import { StyledCheckbox, StyledCheckboxColorIndicator, StyledCheckboxElement, StyledCheckboxHelpTexts, StyledCheckboxIndicator, StyledCheckboxWrapper, StyledLabel, StyledLabelSuffix, StyledLoadingCircle } from './styled';

type InputProps = InputHTMLAttributes<HTMLInputElement>;

export type CheckboxProps = Omit<InputProps, 'placeholder'> & {
	/**
	 * Adds a label to the input field. This is required for accessibilty.
	 */
	label?: string | (() => ReactNode);

	/**
	 * Renders label as HTML if set to true.
	 */
	labelAsHTML?: boolean;

	/**
	 * Add an additional help text below the input field.
	 */
	helpText?: string;

	/**
	 * Add an additional help text below the input field.
	 */
	invalidMessage?: string;

	/**
	 * Show the invalidMessage above checkbox
	 */
	invalidMesssageOnTop?: boolean;

	/**
	 * Set styling to indicate input is invalid.
	 * Also shows the `invalidMessage` if provided
	 */
	isInvalid?: boolean;

	/**
	 * Toggle between checkbox or radio
	 */
	type?: 'checkbox' | 'radio';

	/**
	 * Add suffix to label
	 * Label is required, for the suffix to be shown
	 */
	suffix?: string | number;

	/**
	 * Disable input
	 */
	disabled?: boolean;

	/**
	 * Sets the background color of the checkbox
	 * Supports RGB or HEX colors
	 */
	colorIndicator?: string;

	/**
	 * Sets the checkbox indicator to round
	 */
	isRound?: boolean;

	/**
	 * Appends loadingCircle if fetchResponse is awaiting
	 */
	isLoading?: boolean;
};

export const getCheckmarkValidateOnChangeProps = <T extends FieldValues>(name: FieldPath<T>, options: RegisterOptions<T>, register: UseFormRegister<T>, trigger: UseFormTrigger<T>) => {
	const registeredProps = register(name, options);

	return {
		...registeredProps,
		onChange: async (e: { target: any; type?: any }) => {
			await registeredProps.onChange(e);
			await trigger(name);
		},
	} as UseFormRegisterReturn;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
	(
		{
			label,
			labelAsHTML,
			suffix,
			helpText,
			invalidMessage,
			invalidMesssageOnTop,
			isInvalid,
			id,
			onChange,
			type = 'checkbox',
			disabled,
			colorIndicator,
			isRound,

			className,
			isLoading,
			...rest
		},
		ref) => {
		const [isValidityValid, setIsValidityValid] = useState(true);
		const inputRef = useRef<HTMLInputElement>(null);

		const { fieldId, helpTextId, invalidMessageId, describedById, showHelpText, showInvalidMessage } = useInputField({
			id,
			helpText,
			invalidMessage,
			isInvalid,
		});

		const isValid = !isInvalid && isValidityValid;

		const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
			setIsValidityValid(inputRef.current?.validity.valid ? true : false);
			onChange && onChange(event);
		};

		useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [ref]);

		const renderHelpText = useMemo(
			() => (
				<StyledCheckboxHelpTexts>
					{showInvalidMessage ? (
						<InvalidMessage
							id={invalidMessageId}
							children={invalidMessage}
						/>
					) : null}
					{showHelpText ? (
						<HelpText
							id={helpTextId}
							children={helpText}
						/>
					) : null}
				</StyledCheckboxHelpTexts>
			),
			[showInvalidMessage, invalidMessageId, invalidMessage, showHelpText, helpTextId, helpText],
		);

		return (
			<div className={className}>
				{invalidMesssageOnTop ? renderHelpText : null}
				<StyledCheckboxWrapper
					valid={isValid}
					disabled={disabled}
				>
					<StyledCheckboxElement round={isRound || type === 'radio'}>
						<StyledCheckbox
							type={type}
							id={fieldId}
							ref={inputRef}
							disabled={disabled}
							aria-describedby={describedById}
							onChange={onChangeHandler}
							{...rest}
						/>
						<StyledCheckboxIndicator>{disabled ? <Minus /> : <Selected />}</StyledCheckboxIndicator>
					</StyledCheckboxElement>
					{label && (
						<StyledLabel
							htmlFor={fieldId}
							title={typeof label === 'string' && !labelAsHTML ? label : undefined}
						>
							{colorIndicator ? <StyledCheckboxColorIndicator style={{ background: colorIndicator }} /> : null}
							{typeof label === 'string' ? labelAsHTML ? <RawHtml html={label} /> : label : null}
							{typeof label === 'function' ? label() : null}
							{!!suffix && <StyledLabelSuffix children={suffix} />}
							{isLoading ? <StyledLoadingCircle size="sm" /> : null}
						</StyledLabel>
					)}
				</StyledCheckboxWrapper>
				{!invalidMesssageOnTop ? renderHelpText : null}
			</div>
		);
	},
);
